import { Box, Flex, UnorderedList, ListItem, Text, IconButton } from "@chakra-ui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getKeyFromName, notificationSubtext } from "components/elements/NotificationsList";
import UserAvatar from "components/elements/UserAvatar";
import PageHeader from "components/layout/PageHeader";
import moment from "moment";
import { NotificationsContext } from "providers/notifications";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { numberToDollar } from "utils/helpers";
import { useTenant } from "modules/tenant/providers/tenant-provider";

const notificationTextLong = (user_id: string, notification: any) => {
  const { name: fullName, event } = notification;

  try {
    const name = getKeyFromName(fullName);

    const fromUser = event.user.id === user_id;
    const userLabel = fromUser ? (
      <Text as="span" fontWeight="600">
        You
      </Text>
    ) : (
      <Text as="span" fontWeight="600">
        {event.user.first_name}
      </Text>
    );

    if (name === "updated_loan") {
      if (!event.data.new__status) {
        return <Text fontSize="14px">{userLabel} updated loan details.</Text>;
      }
      return (
        <Text fontSize="14px">
          {userLabel} updated the loan status to{" "}
          <Link to={`/loans/${event.loan.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {/* {LOAN_STATUS_LABELS[event.data.new_status] ||
              `REFORMAT: ${event.data.new_status}`} */}
              {`REFORMAT: ${event.data.new_status}`}
            </Text>
            .
          </Link>
        </Text>
      );
    }

    if (name === "created_item_contact") {
      return (
        <Text fontSize="14px">
          {userLabel} {fromUser ? `assigned yourself` : `assigned you to`} a checklist item:{" "}
          <Link to={`/loans/${event.loan.id}?tab=checklist&item=${event.loan_item.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {event.loan_item.title}
            </Text>
          </Link>
        </Text>
      );
    }

    if (name === "created_loan_contact") {
      return (
        <Text fontSize="14px">
          {userLabel} {fromUser ? `assigned yourself` : `assigned you to`} a loan:{" "}
          <Link to={`/loans/${event.loan.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {event.loan.name}
            </Text>
          </Link>
        </Text>
      );
    }

    if (name === "requested_funds") {
      return (
        <Text fontSize="14px">
          {userLabel} requested proof of funds for{" "}
          <Link to={`/loans/${event.loan.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {numberToDollar(event.data.amount)}
            </Text>
            .
          </Link>
        </Text>
      );
    }
    if (name === "verified_funds") {
      return (
        <Text fontSize="14px">
          {userLabel} verified proof of funds for{" "}
          <Link to={`/loans/${event.loan.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {numberToDollar(event.data.amount)}
            </Text>
            .
          </Link>
        </Text>
      );
    }

    if (name === "updated_draw_request") {
      return (
        <Text fontSize="14px">
          {userLabel} updated a draw request:{" "}
          <Link to={`/loans/${event.loan.id}?tab=payments&draw_request=${event.draw_request?.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {event.draw_request.title}
            </Text>
            .
          </Link>
        </Text>
      );
    }
    if (name === "created_draw_request") {
      return (
        <Text fontSize="14px">
          {userLabel} created a draw request:{" "}
          <Link to={`/loans/${event.loan.id}?tab=payments&draw_request=${event.draw_request?.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {event.draw_request?.title}
            </Text>
            .
          </Link>
        </Text>
      );
    }

    if (name === "updated_signature") {
      return <Text fontSize="14px">{userLabel} requested a signature.</Text>;
    }

    if (name === "uploaded_document") {
      return (
        <Text fontSize="14px">
          {userLabel} uploaded document:{" "}
          {event.document && event.document.id && (
            <Link to={`/documents/${event.document.id}`}>
              <Text as="span" color="primary" fontWeight="600">
                {event.document?.name}
              </Text>
              .
            </Link>
          )}
        </Text>
      );
    }
    if (name === "onboarded") {
      return (
        <Text fontSize="14px">
          <Link to={`/borrowers/${event.user.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {userLabel}
            </Text>
          </Link>{" "}
          completed borrowing onboarding.
        </Text>
      );
    }

    if (name === "kyc_verified") {
      return (
        <Text fontSize="14px">
          <Link to={`/borrowers/${event.user.id}`}>
            <Text as="span" color="primary" fontWeight="600">
              {userLabel}
            </Text>
          </Link>{" "}
          has completed KYC verification.
        </Text>
      );
    }

    if (name === "invited_lender") {
      return (
        <Text fontSize="14px">
          <Text as="span" color="primary" fontWeight="600">
            {userLabel}
          </Text>{" "}
          has been invited.
        </Text>
      );
    }

    if (name === "created_loan") {
      return (
        <Text fontSize="14px">
          <Text as="span" color="primary" fontWeight="600">
            {userLabel}
          </Text>{" "}
          created a loan.
        </Text>
      );
    }

    return (
      <Text fontSize="14px">
        {userLabel}
        {` ERROR: ${name}`}
      </Text>
    );
  } catch {
    return null;
  }
};

function Notifications() {
  const { notifications, dismissNotification } = useContext(NotificationsContext);

  const { user } = useTenant();

  return (
    <>
      <PageHeader title="Notifications" />
      <Flex justifyContent="center">
        <Box w="700px" bg="white" borderRadius="20px" px="38px" py="24px">
          {notifications?.length === 0 ? (
            "You have no notifications"
          ) : (
            <UnorderedList listStyleType="none" p="0px" m="0px">
              {notifications
                .sort(
                  (a: any, b: any) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )
                .map((notification: any) => (
                  <ListItem
                    key={notification.id}
                    borderBottomWidth="1px"
                    borderBottomColor="gray.50"
                    py="15px"
                    _last={{
                      borderBottomWidth: "0px",
                      pb: "0px",
                    }}
                  >
                    <Flex alignItems="center">
                      <UserAvatar size="54px" user={notification.event.user} mr="20px" />
                      <Flex flexDir="column" flexGrow="1">
                        {notificationTextLong(user.id, notification)}
                        <Flex color="gray.200" fontSize="12px" lineHeight="20px" fontWeight="500">
                          <Text>
                            {moment(notification.created_at).fromNow()} &middot;{" "}
                            {notificationSubtext(notification)}
                          </Text>
                        </Flex>
                      </Flex>
                      <IconButton
                        aria-label="notification-actions"
                        onClick={() => dismissNotification(notification.id)}
                        variant="ghost"
                        size="sm"
                        color="gray.200"
                        _hover={{
                          color: "black",
                        }}
                        icon={<FontAwesomeIcon icon={["far", "times"]} />}
                      />
                    </Flex>
                  </ListItem>
                ))}
            </UnorderedList>
          )}
        </Box>
      </Flex>
    </>
  );
}

export default Notifications;
